<script setup>
import { ref, onMounted, nextTick, computed } from "vue";
import { DataTable } from "simple-datatables";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import { CoursesClient } from "@/services/CoursesClient";
import { useStore } from "vuex";

const store = useStore();

const router = useRouter();
const coursesClient = new CoursesClient();

const headers = ["#", "Nombre del Curso", "Categoría", "Estado", "Precio"];
const courses = ref([]); // Add this line
const error = ref(null);
const loading = ref(true);

const fetchCourses = async () => {
  try {
    const roleId = store.state.userData?.roleId;

    let response;
    if (roleId === 1 || roleId === 2) {
      response = await coursesClient.getAllCourses();
    } else {
      response = await coursesClient.getInstructorCoursesById();     
    }

    const rawCourses = response.data ?? response; // fallback si no hay .data
    courses.value = rawCourses.map(course => ({
      id: course.id,
      title: course.titulo,
      duration: 'N/A',
      category: course.categoria_nombre,
      status: course.estado,
      price: `${course.precio} ${course.moneda === 1 ? 'USD' : 'CRC'}`
    }));

  } catch (err) {
    error.value = "No se pudieron cargar los cursos.";
    console.error("Error fetching courses:", err);
  } finally {
    loading.value = false;
  }
};

const deleteCourse = async (courseId) => {
  const result = await Swal.fire({
        title: '¿Eliminar permanentemente?',
        text: "Todos los datos asociados serán eliminados.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545', // Red
        cancelButtonColor: '#6c757d',  // Gray
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        buttonsStyling: false,
        customClass: {
            actions: 'grid grid-cols-2 gap-2 w-full', // Key change for 2 columns
            confirmButton: 'btn btn-danger w-full',
            cancelButton: 'btn btn-secondary w-full'
        },
        reverseButtons: false // Keep original order
    });

  if (result.isConfirmed) {
    try {
      // Call the API to delete the course
      await coursesClient.deleteCourse(courseId)
        .catch(e => {
          if (!e.message.includes('JSON')) throw e; // Ignore JSON parse errors
        });


      // Only remove from local state if API call succeeds
      const index = courses.value.findIndex(course => course.id === courseId);
      if (index !== -1) {
        courses.value.splice(index, 1);
      }

      Swal.fire("¡Eliminado!", "El curso ha sido eliminado.", "success");
    } catch (error) {
      console.error("Error deleting course:", error);
      Swal.fire("Error", "No se pudo eliminar el curso", "error");
    }
  }
};

onMounted(async () => {
  await fetchCourses();
  await nextTick();

  const tableElement = document.getElementById("courses-list");
  if (tableElement) {
    const dataTable = new DataTable(tableElement, {
      searchable: true,
      fixedHeight: true,
      perPage: 15,
      labels: {
        placeholder: "Buscar curso...",
        perPage: "{select} registros por página",
        noRows: "No se encontraron registros",
        info: "Mostrando {start} al {end} de {rows} registros",
      },
    });

    tableElement.addEventListener("click", (event) => {
      if (event.target.classList.contains("edit-action")) {
        const row = event.target.closest("tr");
        if (row) {
          const courseId = row.children[0].textContent.trim();
          router.push({ name: 'Editar Curso', params: { id: courseId } });
        }
      }

      if (event.target.classList.contains("delete-action")) {
        const row = event.target.closest("tr");
        const courseId = row.children[0].textContent.trim();
        deleteCourse(courseId);
      }
    });
  }
});
</script>

<template>
  <div class="container-fluid py-4 mh-600">
    <div class="row">
      <div v-if="loading" class="text-center">
        <p>Cargando...</p>
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>

      <div v-else class="card">
        <div class="p-3 pb-0 card-header">
          <div class="d-flex justify-content-between">
            <h6 class="mb-2"><i class="fas fa-book pe-2"></i>Cursos</h6>
            <button class="btn btn-dark btn-sm mb-0" @click="router.push({ name: 'Editar Curso' })">
              + Nuevo Curso
            </button>

          </div>
        </div>

        <div class="table-responsive pb-4">
          <table id="courses-list" class="table align-items-center mb-0 px-4">
            <thead>
              <tr>
                <th v-for="(header, index) in headers" :key="index"
                  class="text-uppercase text-dark text-xxs font-weight-bolder opacity-7 px-2 py-3">
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="course in courses" :key="course.id" class="row-hover">
                <td class="px-2 py-3 text-xs">{{ course.id }}</td>
                <td class="px-2 py-3">
                  <router-link :to="{ name: 'Pagina Curso', params: { curso: course.id, capitulo: 1 } }"
                    class="project-link text-xs font-weight-bold">
                    <strong>{{ course.title }}</strong><i class="fas fa-external-link-alt fa-xs ms-2"></i>
                  </router-link>
                </td>
                <td class="px-2 py-3">{{ course.category }}</td>
                <td class="px-2 py-3">
                  <span :class="[
                    'badge',
                    'badge-sm',
                    course.status === 1 ? 'badge-info' : 'badge-dark',
                  ]">
                    {{ course.status === 1 ? 'Publico' : 'Oculto' }}
                  </span>
                </td>
                <td class="px-2 py-3">{{ course.price }}</td>
                <td class="px-2 py-3 text-center">
                  <div class="dropdown">
                    <button class="btn btn-link text-lg" type="button" data-bs-toggle="dropdown" aria-expanded="false"
                      @click.stop>
                      ...
                    </button>
                    <ul class="dropdown-menu custom-dropdown p-3" @click.stop>
                      <li>
                        <a class="dropdown-item edit-action">
                          <i class="fas fa-edit pe-2"></i> Editar
                        </a>
                      </li>
                      <hr class="horizontal dark my-2" />
                      <li>
                        <a class="dropdown-item text-danger delete-action">
                          <i class="fas fa-trash pe-2"></i> Eliminar Curso
                        </a>
                      </li>
                    </ul>
                  </div>
                </td>



              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.row-hover {
  transition: background-color 0.3s ease;
  cursor: pointer;
}

.row-hover:hover {
  background-color: #f0f4f8;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  margin: 2rem auto;
  color: #f0f4f8;
}

.project-link:hover {
  text-decoration: underline;
  text-underline-offset: 4px;
  color: cornflowerblue;
}

.dataTable-input,
::v-deep(.dataTable-input) {
  width: 500px !important;
  /* Ajusta el tamaño según lo necesites */
  max-width: 100%;
}

.custom-dropdown {
  width: 200px;
  /* Ajusta al tamaño que desees */
}


/* Estilos para los badges */
.badge {
  padding: 0.4rem 1.3rem;
  font-size: 0.6rem;
  font-weight: 700;
  border-radius: 1rem;
}

.bg-success {
  background-color: #28a745 !important;
}

.bg-danger {
  background-color: #dc3545 !important;
}
</style>
